import * as _ from "lodash-es";

import { isFeatureEligible } from "@services/featureFlagsService.js";

/**
 * Helper to read a primitive node's content value from a group entry field.
 */
const val = (field) => {
  if (!field) {
    return "";
  }
  // toggleInput stores boolean in content
  if (field.type === "toggleInput") {
    return !!field.content;
  }
  return field.content || "";
};

const parseOrder = (field) => {
  const n = Number(val(field));
  return Number.isNaN(n) ? 9999 : n;
};

const sortByOrderAndTitle = (items = []) =>
  _.orderBy(items, ["order", "title"], ["asc", "asc"]);

const normalizeTarget = (target) => {
  if (!target) {
    return "_parent";
  }
  return target;
};

/**
 * Checks if an item should be visible based on feature flag gating.
 */
const shouldIncludeFeatureItem = ({
  featureName,
  shownWhenFeatureEnabled,
  featureFlags,
  featureFlagsError,
}) => {
  if (!featureName) {
    return true;
  }

  const featureData = featureFlags?.find(
    (feature) => feature.featureName === featureName,
  );
  const isEligible = featureData && isFeatureEligible(featureData);

  if (featureFlagsError) {
    return shownWhenFeatureEnabled === false;
  }

  return shownWhenFeatureEnabled != null
    ? shownWhenFeatureEnabled === isEligible
    : isEligible;
};

/**
 * Transforms DP groups (sections, linkItems, vehiclesFeaturedCategories)
 * into the menuSections array shape that TopNav expects.
 * vehiclesFeaturedCategories uses nested groups: each category entry
 * contains an `items` array of vehicle entries inline. */
export function transformToMenuSections({
  sections,
  linkItems,
  vehiclesFeaturedCategories,
  featureFlags = [],
  featureFlagsError = false,
  liveModelYearsBySeries = null,
}) {
  if (!sections?.length) {
    return [];
  }

  const builtSections = sections
    .filter((section) => !val(section.hidden))
    .map((section) => {
      const title = val(section.title);
      const order = parseOrder(section.order);
      const isVehicleDropdown = val(section.isVehicleDropdown);
      const href = val(section.href);

      // Infer section type from data
      const type = href
        ? "directLink"
        : isVehicleDropdown
          ? "vehicles"
          : "links";

      if (type === "vehicles") {
        // Build vehicle categories from nested vehiclesFeaturedCategories
        const categories = sortByOrderAndTitle(
          (vehiclesFeaturedCategories || []).map((cat) => {
            // When useCustomLinks is toggled on at the category level, each vehicle's
            // customHref field takes precedence over the auto-generated /product/:seriesId href.
            const useCustomLinks = val(cat.useCustomLinks);
            const categoryVehicles = (cat.items || [])
              .map((v) => {
                const seriesId = val(v.seriesId);
                const customHref = useCustomLinks ? val(v.customHref) : "";
                const title = val(v.title);
                // Nav vehicle titles lead with the model year ("2026 Camry") and
                // the DP record carries no separate year field, so parse the year
                // out here for consumers that need year and name apart (e.g. the
                // search-page vehicle card's overview fetch and disclaimers).
                const yearMatch = title.match(/^\s*((?:19|20)\d{2})\s+(\S.*)$/);
                const newestLiveYear = seriesId
                  ? liveModelYearsBySeries?.get(seriesId)
                  : null;
                // An explicit live-year map means this vehicle is governed by the
                // shared visibility gate. Omit it when every year is hidden;
                // while the map is unavailable, retain authored navigation.
                if (liveModelYearsBySeries && seriesId && !newestLiveYear) {
                  return null;
                }
                const modelYear =
                  newestLiveYear ?? (yearMatch ? Number(yearMatch[1]) : null);
                const displayName = yearMatch ? yearMatch[2].trim() : title;
                return {
                  id: seriesId || title,
                  title: modelYear ? `${modelYear} ${displayName}` : title,
                  displayName,
                  modelYear,
                  href: customHref || (seriesId ? `/product/${seriesId}` : ""),
                  target: "_self",
                  alt: title,
                  analyticsLabel: title,
                  seriesId,
                  imageSrc: val(v.image) || "",
                  previousSeriesIds: val(v.previousSeriesIds)
                    ? val(v.previousSeriesIds)
                        .split(",")
                        .map((s) => s.trim())
                    : [],
                  order: parseOrder(v.order),
                };
              })
              .filter(Boolean);

            return {
              id: cat.title?.id || val(cat.title),
              title: val(cat.title),
              analyticsLabel: val(cat.title),
              order: parseOrder(cat.order),
              backgroundImage: val(cat.backgroundImage) || "",
              vehicles: sortByOrderAndTitle(categoryVehicles),
            };
          }),
        );

        return {
          id: section.title?.id || title,
          title,
          type: "vehicles",
          order,
          analyticsLabel: title,
          isVehicleDropdown,
          categories,
        };
      }

      if (type === "directLink") {
        return {
          id: section.title?.id || title,
          title,
          type: "links",
          order,
          analyticsLabel: title,
          isVehicleDropdown: false,
          links: [
            {
              id: section.title?.id || title,
              title,
              href,
              target: "_parent",
              order,
              analyticsLabel: title,
              externalLink: false,
            },
          ],
        };
      }

      // Link-type section — filter items belonging to this section by title match
      const sectionLinks = linkItems
        .filter((item) => val(item.sectionTitle) === title)
        .map((item) => {
          const featureName = val(item.featureName);
          const shownWhenFeatureEnabled = val(item.shownWhenFeatureEnabled);

          if (
            !shouldIncludeFeatureItem({
              featureName,
              shownWhenFeatureEnabled,
              featureFlags,
              featureFlagsError,
            })
          ) {
            return null;
          }

          const featureData = featureName
            ? featureFlags.find((f) => f.featureName === featureName)
            : null;

          return {
            id: item.title?.id || val(item.title),
            title: val(item.title),
            description: val(item.description),
            href: val(item.href),
            target: normalizeTarget(val(item.target)),
            order: parseOrder(item.order),
            analyticsLabel: val(item.title),
            externalLink: val(item.externalLink),
            featureName,
            shownWhenFeatureEnabled,
            data: featureData?.data,
          };
        })
        .filter(Boolean);

      return {
        id: section.title?.id || title,
        title,
        type: "links",
        order,
        analyticsLabel: title,
        isVehicleDropdown,
        links: sortByOrderAndTitle(sectionLinks),
      };
    });

  return sortByOrderAndTitle(builtSections).filter(
    (section) => section.id && section.title,
  );
}

/**
 * Decides whether a utility button is visible for the current user.
 *
 * Deterministic either/or gating for feature-flagged buttons:
 *   - while flags are loading, show NOTHING (avoids flashing the wrong item);
 *   - on flags error, fall back to the inverse item (shownWhenFeatureEnabled=false);
 *   - otherwise show the item whose shownWhenFeatureEnabled matches eligibility.
 * Buttons without a featureName are always shown (plain always-on actions).
 */
const includeUtilityButton = ({
  featureName,
  shownWhenFeatureEnabled,
  featureFlags,
  featureFlagsError,
  featureFlagsLoading,
}) => {
  if (!featureName) {
    return true;
  }
  if (featureFlagsLoading) {
    return false;
  }

  const featureData = featureFlags?.find(
    (feature) => feature.featureName === featureName,
  );
  const isEligible = !!(featureData && isFeatureEligible(featureData));

  if (featureFlagsError) {
    // Deliberate fallback: only the inverse item survives a flags failure.
    return shownWhenFeatureEnabled === false;
  }

  return shownWhenFeatureEnabled === isEligible;
};

/**
 * Resolves a utility button's independent location booleans:
 *   showInDesktopHeader, showInMobileHeader, showInMobileMenu
 *
 * (There is no desktop-menu surface in the app, so no showInDesktopMenu.)
 * New records set these toggles explicitly (and they take precedence). Older
 * records are mapped for backward compatibility so nothing disappears before a
 * data migration runs:
 *   - legacy desktopPlacement/mobilePlacement ("header"/"menu"/"hidden");
 *   - oldest single `placement` ("headerIcons"/"hamburgerQuickLinks"/"both").
 * A record with none of these falls back to the previous default
 * (desktop header + mobile menu) so legacy items stay visible pre-migration.
 */
const resolveLocations = (item) => {
  const hasNewFields =
    item.showInDesktopHeader != null ||
    item.showInMobileHeader != null ||
    item.showInMobileMenu != null;

  if (hasNewFields) {
    return {
      showInDesktopHeader: val(item.showInDesktopHeader) === true,
      showInMobileHeader: val(item.showInMobileHeader) === true,
      showInMobileMenu: val(item.showInMobileMenu) === true,
    };
  }

  // Legacy two-field placement.
  const desktopRaw = val(item.desktopPlacement);
  const mobileRaw = val(item.mobilePlacement);
  if (desktopRaw || mobileRaw) {
    return {
      showInDesktopHeader: desktopRaw === "header",
      showInMobileHeader: mobileRaw === "header",
      showInMobileMenu: mobileRaw === "menu",
    };
  }

  // Oldest single-field placement.
  const legacy = val(item.placement);
  if (legacy === "headerIcons") {
    return {
      showInDesktopHeader: true,
      showInMobileHeader: true,
      showInMobileMenu: false,
    };
  }
  if (legacy === "hamburgerQuickLinks") {
    return {
      showInDesktopHeader: false,
      showInMobileHeader: false,
      showInMobileMenu: true,
    };
  }
  if (legacy === "both") {
    return {
      showInDesktopHeader: true,
      showInMobileHeader: false,
      showInMobileMenu: true,
    };
  }

  // Final fallback (prevents legacy records disappearing before migration).
  return {
    showInDesktopHeader: true,
    showInMobileHeader: false,
    showInMobileMenu: true,
  };
};

/**
 * Transforms the DP `utilityButtons` group into the button array consumed by
 * TopNav (desktop/mobile header) and HamburgerMenu (mobile menu). Each surface
 * filters this array independently by the location booleans; array order is the
 * single source of render order (no `order` field). Image content (object or
 * string) is passed through as `imageSrc`/`mobileImageSrc` and resolved to a URL
 * at render time via getVehicleImageUrl(navigationVersion).
 */
export function transformUtilityButtons({
  utilityButtons = [],
  featureFlags = [],
  featureFlagsError = false,
  featureFlagsLoading = false,
}) {
  if (!utilityButtons.length) {
    return [];
  }

  // Preserve the authored list order — no sorting.
  return utilityButtons
    .map((item) => {
      const featureName = val(item.featureName);
      // toggleInput → boolean via val(); coerce so a missing node is a strict false.
      const shownWhenFeatureEnabled =
        val(item.shownWhenFeatureEnabled) === true;
      const popupEnabled = val(item.popupEnabled) === true;

      if (
        !includeUtilityButton({
          featureName,
          shownWhenFeatureEnabled,
          featureFlags,
          featureFlagsError,
          featureFlagsLoading,
        })
      ) {
        return null;
      }

      const label = val(item.label);

      return {
        id: item.label?.id || label,
        label,
        href: val(item.href),
        target: normalizeTarget(val(item.target)),
        imageSrc: val(item.image) || "",
        mobileImageSrc: val(item.mobileImage) || "",
        ...resolveLocations(item),
        analyticsLabel: val(item.analyticsLabel) || label,
        featureName,
        shownWhenFeatureEnabled,
        popup: popupEnabled
          ? {
              title: val(item.popupTitle),
              body: val(item.popupBody),
              ctaLabel: val(item.popupCtaLabel),
              ctaHref: val(item.popupCtaHref),
              ctaTarget: normalizeTarget(val(item.popupCtaTarget)),
            }
          : null,
      };
    })
    .filter(Boolean)
    .filter((b) => b.id && b.label);
}
